/**
 * TenantActionDialog — una acción de ciclo de vida sobre un cliente desde la
 * consola (OT-0073 §4.1 y §4.3).
 *
 * Toda acción pide motivo (queda en la auditoría del cliente). Extender la
 * beta pide la nueva fecha y otorgar crédito el monto. Suspender y convertir
 * a cliente de pago son de alto impacto: piden además escribir el
 * identificador del cliente para confirmar. Las reglas (fechas, topes,
 * transiciones válidas) las aplica el backend; su error se muestra tal cual.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { TenantLifecycleAction } from '../../api/platform'
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage'
import { usePlatformTenantAction } from '../../hooks/usePlatform'
import { startOfLocalDay } from '../../lib/organizationState'
import { Dialog } from '../Dialog'

export type TenantActionKind = TenantLifecycleAction['kind']

/** Acciones que exigen escribir el identificador para confirmar, con su tono
 * (los de `ConfirmDialog`): suspender bloquea a la organización; convertir a
 * pago no, pero termina la beta. */
const HIGH_IMPACT_TONE: Partial<Record<TenantActionKind, { notice: string; submit: string }>> = {
  suspend: {
    notice: 'bg-critical-soft text-critical',
    submit: 'bg-critical hover:bg-critical-deep',
  },
  convert_to_active: {
    notice: 'bg-review-soft text-review',
    submit: 'bg-review hover:bg-review-deep',
  },
}

interface TenantActionDialogProps {
  tenantId: string
  slug: string
  kind: TenantActionKind | null
  onClose: () => void
}

export function TenantActionDialog({ tenantId, slug, kind, onClose }: TenantActionDialogProps) {
  const { t } = useTranslation('platform')
  return (
    <Dialog
      open={kind !== null}
      onClose={onClose}
      title={kind ? t(`clients.action.${kind}.title`) : ''}
    >
      {kind ? (
        <TenantActionForm key={kind} tenantId={tenantId} slug={slug} kind={kind} onClose={onClose} />
      ) : null}
    </Dialog>
  )
}

const INPUT =
  'w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
const LABEL = 'mb-1 block text-xs font-medium text-ink'

export function TenantActionForm({
  tenantId,
  slug,
  kind,
  onClose,
}: {
  tenantId: string
  slug: string
  kind: TenantActionKind
  onClose: () => void
}) {
  const { t } = useTranslation(['platform', 'common'])
  const errorMessage = useApiErrorMessage()
  const action = usePlatformTenantAction(tenantId)

  const [reason, setReason] = useState('')
  const [until, setUntil] = useState('')
  const [amount, setAmount] = useState('')
  const [confirmation, setConfirmation] = useState('')

  const busy = action.isPending
  const highImpact = HIGH_IMPACT_TONE[kind]
  const needsConfirmation = highImpact !== undefined
  const canSubmit =
    !busy &&
    reason.trim() !== '' &&
    (kind !== 'extend_trial' || until !== '') &&
    (kind !== 'grant_credit' || amount !== '') &&
    (!needsConfirmation || confirmation === slug)

  const payload = (): TenantLifecycleAction => {
    switch (kind) {
      case 'extend_trial':
        // La beta vence al empezar el día elegido, en la zona del operador; así
        // la consola y el pie del cliente muestran ese mismo día (OT-0073).
        return { kind, until: startOfLocalDay(until), reason }
      case 'grant_credit':
        return { kind, amount_usd: amount, reason }
      default:
        return { kind, reason }
    }
  }

  const submit = () => {
    action.mutate(payload(), { onSuccess: onClose })
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (canSubmit) submit()
      }}
      className="space-y-4"
    >
      {highImpact ? (
        <p className={`rounded-md px-3 py-2 text-sm ${highImpact.notice}`}>
          {t(`clients.action.${kind}.warning`)}
        </p>
      ) : null}

      {kind === 'extend_trial' ? (
        <div>
          <label htmlFor="action-until" className={LABEL}>
            {t('clients.action.until')}
          </label>
          <input
            id="action-until"
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            aria-describedby="action-until-help"
            className={`${INPUT} font-mono`}
            disabled={busy}
            autoFocus
          />
          <p id="action-until-help" className="mt-1 text-[11px] text-ink-subtle">
            {t('clients.action.until_help')}
          </p>
        </div>
      ) : null}

      {kind === 'grant_credit' ? (
        <div>
          <label htmlFor="action-amount" className={LABEL}>
            {t('clients.action.amount_usd')}
          </label>
          <input
            id="action-amount"
            type="number"
            min={0.01}
            step={0.01}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={`${INPUT} font-mono`}
            disabled={busy}
            autoFocus
          />
        </div>
      ) : null}

      <div>
        <label htmlFor="action-reason" className={LABEL}>
          {t('clients.action.reason')}
        </label>
        <textarea
          id="action-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('clients.action.reason_placeholder')}
          className={INPUT}
          disabled={busy}
          autoFocus={kind !== 'extend_trial' && kind !== 'grant_credit'}
        />
        <p className="mt-1 text-[11px] text-ink-subtle">{t('clients.action.reason_help')}</p>
      </div>

      {needsConfirmation ? (
        <div>
          <label htmlFor="action-confirm" className={LABEL}>
            {t('clients.action.confirm_slug', { slug })}
          </label>
          <input
            id="action-confirm"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            autoComplete="off"
            className={`${INPUT} font-mono`}
            disabled={busy}
          />
        </div>
      ) : null}

      {action.error ? (
        <p role="alert" className="text-sm text-critical">
          {errorMessage(action.error)}
        </p>
      ) : null}

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="rounded-md border border-line bg-surface px-3 py-1.5 text-sm text-ink hover:bg-surface-muted disabled:opacity-50"
        >
          {t('common:actions.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className={`rounded-md px-3 py-1.5 text-sm font-medium text-canvas disabled:opacity-50 ${
            highImpact?.submit ?? 'bg-brand hover:bg-brand/90'
          }`}
        >
          {t(`clients.action.${kind}.submit`)}
        </button>
      </div>
    </form>
  )
}
